import {
    compose,
    map,
    toLower,
    unnest,
    partition,
    append,
    isEmpty,
} from 'ramda';

import {
    unnestRemoveEmpty,
    replaceSpacesWithDashes,
    replaceDashSlash,
    replaceSlashDash,
    replaceAmpersandWithAnd,
    replacePeriodWithDash,
    pluralizeCylinder,
    mergePlugIn,
    sumPointsByGroup,
    joinWithDashes,
    propValue,
    sortTakeTwoJoinDash,
    formatMake,
    formatModel,
    formatYear,
    translateOptions,
    countColorsPoints,
    countNonTaxonomyPoints,
    countTrimsPoints,
    countLocationPoints,
    filterTruthy,
    hasLength,
    preProcessing,
    withSortTermLocale,
    sortMapJoinTerms,
    takeTwoDashTerm,
    sortAcummulatedTerms,
} from '../SharedRules';

import { RouteSegment } from '../RouteSegment';
import { encodeAndStringify } from '../../../../shared/crypto';

import { routeGroups } from '../RouteGroups';

// aliases
const getNonEmptyUrl = filterTruthy;
const getColors = sortMapJoinTerms;
const getTrim = sortMapJoinTerms;

export const getBodyStyle = ({ data: { terms: bodyStyles} }) => {
    return RouteSegment.getSegmentWhen(
        hasLength(bodyStyles),
        () => `${withSortTermLocale(bodyStyles)[0].term}`,
        routeGroups.taxonomy,
        bodyStyles.length
    );
};

export const getColorSegment = ({ data: { terms: colors} }) => {
    return RouteSegment.getSegmentWhen(
        hasLength(colors),
        () => `${getColors(colors)}`,
        routeGroups.colors,
        colors.length
    );
};

export const getTrimsSegment = ({data: { terms: trims}}) => {
    return RouteSegment.getSegmentWhen(
        hasLength(trims),
        () => `${getTrim(trims)}`,
        routeGroups.trims,
        trims.length
    );
};

const hasMinMax = obj => obj && obj.min && obj.max;

const isValidYear = year => year
    && year.ranges
    && year.ranges.length
    && Object.keys(year.ranges[0]).length;

const hasYearRange = year => {
    const proper = isValidYear(year)
        && hasMinMax(year.ranges[0]);

    const firstYear = year.ranges[0];

    return proper
        && firstYear.min
        && firstYear.max
        && firstYear.min !== firstYear.max;
};

export const getYearsSegment = ({ data: year }) => {
    const isYearRange = hasYearRange(year)

    return RouteSegment.getSegmentWhen(
        isValidYear(year),
        () => formatYear(year.ranges[0], isYearRange),
        routeGroups.levelOne,
        isYearRange ? 2 : 1
    );
};

export const getOptionsSegment = ({ data: options }) => {
    const accumulatedOptions = sortAcummulatedTerms(options);
    const res = translateOptions(accumulatedOptions[1]);

    return RouteSegment.getSegmentWhen(res, () => `${sortTakeTwoJoinDash(res)}`, routeGroups.options, res.length);
};

export const getLocationUrlSegment = ({ data: location }) => RouteSegment.getSegmentWhen(
    location && location.city,
    () => `in-${replaceSpacesWithDashes(location.city)}-${location.state}`,
    routeGroups.location
);

export const getFeaturesSegment = ({ data: { terms: features}, rulesState }) => {
    if (rulesState.flags.isFreeDelivery) {
        features = [...features, {term: 'free-shipping'}]
    }

    return RouteSegment.getSegmentWhen(
        features && features.length,
        () => `with-${takeTwoDashTerm(features)}`,
        routeGroups.features,
        features.length
    );
}

export const getSegmentForSingleModelParentMakeSelect = () => RouteSegment.getSegmentWhen(false, () => ``, routeGroups.taxonomy);

export const getSegmentForSingleModelSelect = ({ rulesState }) => RouteSegment.getSegmentWhen(
    rulesState.flags.isSingleModel,
    () => `${formatMake(rulesState.firstMake)}-${formatModel(rulesState.firstModelName)}`,
    routeGroups.taxonomy
);


export const getSegmentForMultiMakeModel = ({ rulesState }) => RouteSegment.getSegmentWhen(
    rulesState.flags.isMultiMake || rulesState.flags.isMultiModel,
    () => rulesState.firstModelName ? `${formatMake(rulesState.firstMake)}-${formatModel(rulesState.firstModelName)}` : `${formatMake(rulesState.firstMake)}`,
    routeGroups.taxonomy
);


export const getSegmentForAllModelSelect = ({ rulesState }) => RouteSegment.getSegmentWhen(
    rulesState.flags.isAllModels,
    () => `${formatMake(rulesState.firstMake)}`,
    routeGroups.taxonomy
);


export const getSegmentForNoModelSelect = ({ rulesState }) => RouteSegment.getSegmentWhen(
    rulesState.flags.isNoModels,
    () => `${formatMake(rulesState.firstMake)}`,
    routeGroups.taxonomy
);


// Postprocessing
const formatUrl = compose(
    replaceSlashDash,
    replaceDashSlash,
    replaceAmpersandWithAnd,
    replacePeriodWithDash,
    pluralizeCylinder,
    mergePlugIn,
    joinWithDashes
);

export const finalizeUrl = segments => ({
    segments,
    pathname: formatUrl(segments)
});


const takeValues = map(propValue);

const matchesGroup = group => x => x.group && x.group === group;
const isTaxonomyGroup = matchesGroup(routeGroups.taxonomy);
const countFeaturesPoints = sumPointsByGroup(routeGroups.features);
const countOptionsPoints = sumPointsByGroup(routeGroups.options);
const slashSegment = RouteSegment.getSegment(() => '/', '', 0);
const appendSlash = compose(append(slashSegment), unnest);

const formatSegments = compose(map(toLower), getNonEmptyUrl, takeValues);
const withTaxonomyPartition = compose(partition(isTaxonomyGroup), unnestRemoveEmpty);
const finishFormatting = compose(finalizeUrl, formatSegments, unnest);

export const postProcessing = ({ results, rulesState }) => {
    const nonTaxonomyPoints = countNonTaxonomyPoints(results);

    rulesState.flags.nonTaxonomyPoints = countNonTaxonomyPoints(results);
    rulesState.flags.featuresPoints = countFeaturesPoints(results);
    rulesState.flags.optionsPoints = countOptionsPoints(results);
    rulesState.flags.colorsPoints = countColorsPoints(results);
    rulesState.flags.trimsPoints = countTrimsPoints(results);
    rulesState.flags.locationPoints = countLocationPoints(results);

    const parted = withTaxonomyPartition(results);

    let cvnaid;

    if (rulesState.requiresCvnaid()) {
        parted[1] = [RouteSegment.getSegment(() => 'filters/', routeGroups.levelTwo, nonTaxonomyPoints)];
        cvnaid = rulesState.config.selectedFiltersForApi
            && !isEmpty(rulesState.config.selectedFiltersForApi)
                ? encodeAndStringify(rulesState.config.selectedFiltersForApi)
                : '';
    }

    const requiresSlash = parted && parted[0] && parted[0].length && parted[1] && parted[1].length;

    if (requiresSlash) {
        parted[0] = appendSlash(parted[0]);
    }

    return {
        ...finishFormatting(parted),
        flags: rulesState.flags,
        cvnaid,
        search: cvnaid ? `?cvnaid=${cvnaid}` : ''
    };
}

export const preProcessingUrls = ({selectedFilters, rulesState}) => {
    preProcessing({selectedFilters, rulesState});
};
